import isEmpty from 'lodash/isEmpty'
import { useCallback, useState } from 'react'
import { toast } from 'react-toastify'
import Filters from '../components/Filters'
import ProductGrid from '../components/ProductGrid'
import Layout, { LayoutHeaderSlot } from '../layout/Layout'
import Subheader from '../components/Subheader'
import { useDebouncedCallback } from 'use-debounce'
import useLocalStorageState from 'use-local-storage-state'
import ColumnChooserPopover from '../components/ColumnChooserPopover'
import SellerTracking from '../containers/SellerTracking'
import CommonModal from '../components/CommonModal'
import RegionManager from '../components/RegionManager'
import { useColumnSettingsLoader } from '../hooks/useColumnSettingsSync'
import { exportSpyListAPI } from '../lib/api/api'
import { ReactComponent as ExportIcon } from '../icons/export.svg'
import Tippy from '@tippyjs/react'
import styles from './SpySearch.module.css'

const parseFilenameFromHeader = (header) => {
  if (typeof header !== 'string') {
    return null
  }

  const filenameStarMatch = header.match(/filename\*=([^;]+)/i)
  if (filenameStarMatch && filenameStarMatch[1]) {
    const rawValue = filenameStarMatch[1].split("''").pop() || filenameStarMatch[1]
    try {
      return decodeURIComponent(rawValue.replace(/['"]/g, '').trim())
    } catch (error) {
      return rawValue.replace(/['"]/g, '').trim()
    }
  }

  const filenameMatch = header.match(/filename="?([^";]+)"?/i)
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].trim()
  }

  return null
}

// Reihenfolge spiegelt die Spalten-Definition in ProductGrid (allColumns):
// nach einem Reset (keine gespeicherte Order) zeigt der Column Chooser
// die Liste in GENAU dieser Reihenfolge an.
const COLUMNS = [
  ['amz_img', 'Amazon img'],
  ['actions', 'Befehle'],
  ['keepa_chart_dk', 'Chart'],
  ['flags', 'Flaggen'],
  ['margin', 'Marge'],
  ['roi', 'Roi'],
  ['profit', 'Profit'],
  ['salesrank', 'BSR'],
  ['percent_bsr', 'BSR%'],
  ['asin', 'Asin'],
  ['supplier_url', 'Supplier'],
  ['name', 'Title'],
  ['ean', 'EAN'],
  ['sellprice', 'Sellprice'],
  ['seller_name', 'Seller'],
  ['confirmed_sales', 'Conf. Sales'],
  ['salesdrops', 'Salesdrops'],
  ['variations', 'Variations'],
  ['buyprice', 'Buyprice'],
  ['timestamp', 'Timestamp'],
]

const application = 'spy'

export default function SpySearch() {
  let [regions, setRegions] = useState(['de'])

  let [search, setSearch] = useState('')
  let onSearchDebounced = useDebouncedCallback((value) => setSearch(value), 500)
  // Kontrollierter Wert nur fuer die Such-Pille (Clear-Button);
  // das eigentliche Suchen laeuft weiter debounced ueber setSearch.
  let [searchText, setSearchText] = useState('')
  const handleSearch = (value) => {
    setSearchText(value)
    onSearchDebounced(value)
  }

  let [hiddenColumnIds, setHiddenColumnIds] = useLocalStorageState(`${application}-col-hidden`, {
    defaultValue: []
  })

  let [columnOrder, setColumnOrder] = useLocalStorageState(`${application}-col-order`)
  // Gleicher Key wie in ProductGrid (use-local-storage-state synct) --
  // der Ansicht-Switch lebt jetzt im ColumnChooserPopover.
  let [viewMode, setViewMode] = useLocalStorageState(`${application}-view-mode`, {
    defaultValue: 'table'
  })
  let [columnSizing, setColumnSizing] = useLocalStorageState(`${application}-col-sizing`, {
    defaultValue: {}
  })

  useColumnSettingsLoader({
    module: application,
    setColumnOrder,
    setHiddenColumnIds,
    setColumnSizing,
  })

  let [isOpen, setIsOpen] = useState(false)
  let [actionsOpen, setActionsOpen] = useState(false)
  let [isExporting, setIsExporting] = useState(false)

  const handleExport = useCallback(async () => {
    if (isExporting) {
      return
    }
    setIsExporting(true)
    try {
      const response = await exportSpyListAPI(regions)
      const blobData = response?.data
      if (!(blobData instanceof Blob)) {
        toast.error('Unerwartetes Export-Format erhalten.')
        return
      }

      const headerFileName = parseFilenameFromHeader(response?.headers?.['content-disposition'])
      const url = window.URL.createObjectURL(blobData)
      const link = document.createElement('a')
      link.href = url
      link.download = headerFileName || 'spy_export.xlsx'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Export wird gestartet.')
    } catch (error) {
      console.error('Error exporting spy list', error)
    } finally {
      setIsExporting(false)
      // Menue bleibt waehrend des Exports offen (Spinner als Feedback) und
      // schliesst erst, wenn der Download angestossen bzw. abgebrochen ist.
      setActionsOpen(false)
    }
  }, [isExporting, regions])

  return (
    <Layout scrollWithHeader>
      <section className="overflow-hidden w-full h-full flex flex-col">
        {/* Einziger Scroller der Seite (vertikal + horizontal): die EDGE-Leiste
            (LayoutHeaderSlot) scrollt vertikal mit weg, das Seiten-Chrome bleibt
            per sticky left beim Quer-Scrollen stehen, die thead-Spaltennamen
            kleben per sticky top am oberen Rand. */}
        <div className="flex-1 min-h-0 flex flex-col overflow-auto no-h-scrollbar" style={{ overflowAnchor: 'none' }}>
          <LayoutHeaderSlot />
          <div className="sticky left-0 z-20 bg-background">
        <header className="px-4 pt-4 pb-5">
          <Subheader date>
            Spy <em>Search</em>
          </Subheader>

          {/* Toolbar-Bar: Controls in eigenem umrandeten Balken. Suche fuellt
              links (gekappt), Buttons + CTA rechts. */}
          <div className='mt-5 flex flex-wrap items-center gap-2.5 p-2.5 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-bgColor dark:bg-white/[0.02]'>
            {/* Such-Pille — fuellt den Platz links */}
            <div className="relative flex-1 min-w-[220px] max-w-[380px] group">
              <i className="bx bx-search absolute left-3 top-1/2 -translate-y-1/2 text-base text-neutral-400 pointer-events-none group-focus-within:text-primary"></i>
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Produkt, ASIN oder EAN suchen"
                className="w-full h-9 pl-9 pr-9 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-background text-sm text-textTitle placeholder:text-neutral-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => handleSearch('')}
                  aria-label="Leeren"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded text-neutral-400 hover:text-textTitle"
                >
                  <i className="bx bx-x text-lg leading-none"></i>
                </button>
              )}
            </div>

            <span className='flex-1'></span>

            <RegionManager regions={regions} setRegions={setRegions} />

            <ColumnChooserPopover
              application='spy'
              // Card View hat keine konfigurierbaren Spalten -> Sektion weg
              columns={viewMode === 'cards' ? null : COLUMNS}
              viewOptions={{
                value: viewMode,
                onChange: setViewMode,
                options: [
                  { value: 'table', label: 'Multiview' },
                  { value: 'cards', label: 'Card View' },
                ],
              }}
              onReset={(!isEmpty(hiddenColumnIds) || !!columnOrder || !isEmpty(columnSizing)) && (() => {
                setHiddenColumnIds([])
                setColumnOrder(null)
                setColumnSizing({})
              })}
            >{({ setIsOpen, isOpen }) =>
              <button
                type='button'
                onClick={() => setIsOpen(val => !val)}
                className={`relative inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors ${isOpen ? 'border-primary text-primary bg-secondary' : 'border-neutral-200 dark:border-neutral-700 text-textTitle bg-background hover:border-primary'}`}
              >
                <i className='bx bx-slider-alt text-[17px] leading-none' aria-hidden></i>
                Ansicht
                <i className={`bx bx-chevron-down text-base leading-none -ml-0.5 ${isOpen ? 'text-primary' : 'text-neutral-400'}`} aria-hidden></i>
              </button>}
            </ColumnChooserPopover>

            {/* Primär-CTA (Mint-Gradient) — Dropdown mit Seller-Tracking + Export.
                Ins body portalen wie das Ansicht-Popover (sticky z-20 Stacking-
                Context des Headers, siehe Kommentar in ColumnChooserPopover). */}
            <Tippy
              visible={actionsOpen}
              onClickOutside={() => setActionsOpen(false)}
              placement='bottom-end'
              interactive={true}
              arrow={false}
              appendTo={() => document.body}
              className={styles.ctaMenu}
              content={
                <div className='flex flex-col gap-0.5 w-56 font-medium'>
                  <button
                    type='button'
                    onClick={() => { setActionsOpen(false); setIsOpen(true) }}
                    className='w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-textTitle hover:bg-neutral-100 dark:hover:bg-neutral-800'
                  >
                    <i className='bx bx-plus text-[18px] leading-none'></i>
                    Seller hinzufügen
                  </button>
                  <button
                    type='button'
                    onClick={handleExport}
                    disabled={isExporting}
                    className='w-full flex items-center gap-2.5 px-2.5 py-2 rounded-md text-sm text-textTitle hover:bg-neutral-100 dark:hover:bg-neutral-800 disabled:opacity-50 disabled:cursor-not-allowed'
                  >
                    {isExporting ? (
                      <i className='bx bx-loader-alt animate-spin text-base leading-none'></i>
                    ) : (
                      <ExportIcon className='h-4 w-auto' />
                    )}
                    {isExporting ? 'Exportiere…' : 'Deals exportieren'}
                  </button>
                </div>
              }
            >
              <button
                type='button'
                onClick={() => setActionsOpen(val => !val)}
                className='inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-gradient-to-br from-primary to-main text-white dark:text-black text-sm font-semibold hover:brightness-105 active:scale-[.98] transition'
              >
                <i className="bx bx-plus text-[18px] leading-none"></i>
                Hinzufügen
              </button>
            </Tippy>
          </div>
        </header>
        <div className='px-4 pb-2'>
          <Filters application='spy' onChange={() => { }} />
        </div>
          </div>
          <ProductGrid
            application={application}
            regions={regions}
            search={search}
            hiddenColumnIds={hiddenColumnIds}
            outerScroll
          />
        </div>
      </section>

      <CommonModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        closeModal={() => setIsOpen(false)}>
        <SellerTracking closeModal={() => setIsOpen(false)} />
      </CommonModal>
    </Layout>
  )
}
